import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { productService } from '../services/productService';
import Swal from 'sweetalert2';

const ProductGrid = () => {
    const { addToCart } = useCart();
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const hasFetched = useRef(false);

    // Função para truncar texto
    const truncateText = (text, maxLength = 80) => {
        if (!text) return '';
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    useEffect(() => {
        // Evita chamadas duplicadas causadas pelo React.StrictMode
        if (hasFetched.current) {
            return;
        }
        
        hasFetched.current = true;
        
        const fetchProducts = async () => {
            try {
                setLoading(true);
                const apiProducts = await productService.getPublicProducts();
                const formattedProducts = productService.formatProductsForFrontend(apiProducts);
                setProducts(formattedProducts);
            } catch (err) {
                console.error('Erro ao carregar produtos:', err);
                setError('Erro ao carregar produtos. Tente novamente.');
            } finally {
                setLoading(false);
            }
        };

        fetchProducts();
    }, []);

    if (loading) {
        return (
            <section className="wrapper style2">
                <div className="container">
                    <div style={{
                        textAlign: 'center',
                        padding: '60px 20px',
                        fontSize: '1.2rem',
                        color: '#666'
                    }}>
                        <i className="fas fa-spinner fa-spin" style={{ marginRight: '10px' }}></i>
                        Carregando produtos...
                    </div>
                </div>
            </section>
        );
    }

    if (error) {
        return (
            <section className="wrapper style2">
                <div className="container">
                    <div style={{
                        textAlign: 'center',
                        padding: '60px 20px',
                        backgroundColor: '#ffebee',
                        border: '1px solid #ffcdd2',
                        borderRadius: '8px',
                        color: '#c62828'
                    }}>
                        <i className="fas fa-exclamation-triangle" style={{ marginRight: '10px' }}></i>
                        {error}
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="wrapper style2">
            <div className="container">
                <div className="row gtr-150">
                    {products.map((product) => (
                        <div key={product.id} className="col-4 col-12-narrower">
                            <a
                                href={`/product/${product.id}`}
                                style={{
                                    textDecoration: 'none !important',
                                    color: 'inherit',
                                    display: 'block',
                                    outline: 'none',
                                    border: 'none'
                                }}
                            >
                                <section
                                    className="product-card"
                                    style={{
                                        backgroundColor: '#ffffff',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '8px',
                                        padding: '20px',
                                        margin: '10px 0',
                                        transition: 'all 0.3s ease',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        height: '100%'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-5px)';
                                        e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.15)';
                                        e.currentTarget.style.borderColor = '#000000';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                                        e.currentTarget.style.borderColor = '#e0e0e0';
                                    }}
                                >
                                    <span className="image featured" style={{ marginBottom: '15px', display: 'block' }}>
                                        <img
                                            src={product.image}
                                            alt={product.alt}
                                            style={{
                                                width: '100%',
                                                aspectRatio: '1 / 1',
                                                objectFit: 'cover',
                                                borderRadius: '6px'
                                            }}
                                        />
                                    </span>
                                    <h3 style={{
                                        margin: '0 0 15px 0',
                                        fontSize: '1.5rem',
                                        color: '#000000',
                                        fontWeight: 'bold',
                                        textAlign: 'center',
                                        textTransform: 'uppercase',
                                        letterSpacing: '1px',
                                        textShadow: '1px 1px 2px rgba(0,0,0,0.1)',
                                        padding: '10px 0',
                                        lineHeight: '1.3'
                                    }}>
                                        {product.title}
                                    </h3>
                                    <p style={{
                                        margin: '0 0 15px 0',
                                        color: '#666',
                                        lineHeight: '1.5',
                                        fontSize: '0.95rem'
                                    }}>
                                        {truncateText(product.description)}
                                    </p>

                                    {/* Preço do produto */}
                                    <div style={{
                                        margin: '0 0 15px 0',
                                        textAlign: 'center'
                                    }}>
                                        {product.originalPrice ? (
                                            <div style={{
                                                backgroundColor: '#f8f8f8',
                                                padding: '12px',
                                                borderRadius: '6px',
                                                border: '2px solid #e0e0e0',
                                                lineHeight: '1.4'
                                            }}>
                                                {/* Preço original riscado */}
                                                <div style={{
                                                    fontSize: '0.9rem',
                                                    color: '#999',
                                                    textDecoration: 'line-through',
                                                    marginBottom: '4px'
                                                }}>
                                                    R$ {product.originalPrice.toFixed(2).replace('.', ',')}
                                                </div>
                                                {/* Preço atual em destaque */}
                                                <div style={{
                                                    fontSize: '1.4rem',
                                                    fontWeight: 'bold',
                                                    color: '#000000'
                                                }}>
                                                    R$ {product.currentPrice.toFixed(2).replace('.', ',')}
                                                </div>
                                            </div>
                                        ) : (
                                            <div style={{
                                                fontSize: '1.4rem',
                                                fontWeight: 'bold',
                                                color: '#000000',
                                                backgroundColor: '#f8f8f8',
                                                padding: '12px',
                                                borderRadius: '6px',
                                                border: '2px solid #e0e0e0'
                                            }}>
                                                R$ {product.currentPrice.toFixed(2).replace('.', ',')}
                                            </div>
                                        )}
                                    </div>

                                    <div style={{
                                        marginTop: 'auto',
                                        paddingTop: '15px',
                                        textAlign: 'center'
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            gap: '8px'
                                        }}>
                                            <button
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    addToCart(product);
                                                    Swal.fire({
                                                        title: 'Produto Adicionado!',
                                                        text: `"${product.title}" foi adicionado ao carrinho`,
                                                        icon: 'success',
                                                        showCancelButton: true,
                                                        confirmButtonText: 'Continuar Comprando',
                                                        cancelButtonText: 'OK',
                                                        confirmButtonColor: '#edf507',
                                                        cancelButtonColor: '#6c757d',
                                                        customClass: {
                                                            confirmButton: 'swal-confirm-button',
                                                            cancelButton: 'swal-cancel-button'
                                                        }
                                                    }).then((result) => {
                                                        if (result.isConfirmed) {
                                                            navigate('/');
                                                        }
                                                    });
                                                }}
                                                style={{
                                                    flex: '1',
                                                    padding: '12px 15px',
                                                    backgroundColor: '#edf507',
                                                    color: '#000000',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    fontSize: '1.2rem',
                                                    fontWeight: 'bold',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.target.style.backgroundColor = '#dce406';
                                                    e.target.style.transform = 'scale(1.05)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.backgroundColor = '#edf507';
                                                    e.target.style.transform = 'scale(1)';
                                                }}
                                            >
                                                <i className="fas fa-shopping-cart"></i>
                                            </button>
                                            <div style={{
                                                flex: '2',
                                                padding: '12px 15px',
                                                backgroundColor: '#000000',
                                                color: '#ffffff',
                                                borderRadius: '6px',
                                                fontSize: '0.9rem',
                                                fontWeight: 'bold',
                                                textTransform: 'uppercase',
                                                letterSpacing: '1px'
                                            }}>
                                                Ver Detalhes
                                            </div>
                                        </div>
                                    </div>
                                </section>
                            </a>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default ProductGrid;
